import {useContext} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Grid, Typography, SxProps, Box} from '@mui/material';

import {DepartmentDisplay} from '../DepartmentsDisplay';

import MenuButtonActions from './ButtonActions/MenuButtonActions';
import {Column} from '../../../../../../components/Column/Column';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import {EmpionTooltip} from '../../../../../../components/EmpionTooltip/EmpionTooltip';
import {ErrorMessage} from '../../../../../../components/ErrorMessage/ErrorMessage';
import {JobPostingStatus} from '../../../../../../components/JobPostingDetails/JobPostingStatus';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {empionGreen, empionOrange} from '../../../../../../layouts/theme/EmpionColors';
import {Patch} from '../../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../../lib/api/PATCH/PatchRoutes';
import {transformDate} from '../../../../../../utils/dateUtils/dateFormattingUtils';
import * as utils from '../../../../../../utils/jobPostingUtils/jobPostingUtils';
import {IconHeader, JobDetailsIcon} from '../../../../../applicant/matches/components/IconHeader/IconHeader';
import JobPostingsSerializer from '../../serializers/JobPostingSerializers';
import {CompanyLocation, JobPosting} from '../../types/JobPostingsTypes';
interface JobPostingAccordionDetailsHeaderProps {
  jobPosting: JobPosting;
  renderJobPostingLocations: (jobPosting: JobPosting) => any;
  companyId: string;
  handleUpdateJobPosting(jobPosting: JobPosting): void;
  handleDeleteJobPosting(jobPosting: JobPosting): void;
  handleJobPostingStatusChange(jobPosting: JobPosting | undefined, action: 'archive' | 'activate'): void;
  companyLocations: CompanyLocation[];
  renderIconStatus(jobPosting: JobPosting): any;
}

export default function JobPostingsAccordionDetailsHeader({
  jobPosting,
  companyId,
  handleUpdateJobPosting,
  handleDeleteJobPosting,
  handleJobPostingStatusChange,
  companyLocations,
}: JobPostingAccordionDetailsHeaderProps) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const iconColor = empionOrange[70];

  const currentLanguage = useContext(LanguageContext);

  const locationsAndDateColorBody1 = empionGreen[100];

  const buttonThemeColour: SxProps = {
    color: 'black',
    backgroundColor: empionOrange[4],
    '&:hover': {
      backgroundColor: empionGreen[40],
    },
    boxShadow: 'none',
  };

  const handleViewApplicantsClick = () => {
    navigate('/company/recruiting/applicants', {
      state: {jobPostingId: jobPosting.resourceId},
    });
  };

  const {makeRequest: activateJobPosting, error: activateJobPostingError} = useApiWrite<
    Patch.ActivateJobPosting,
    JobPosting
  >({
    method: 'patch',
    path: PatchRoutes[Patch.ActivateJobPosting](companyId, jobPosting.resourceId as string),
    onSuccess: (data: JobPosting) => {
      handleJobPostingStatusChange(data, 'activate');
    },
    camelCaseOverride: JobPostingsSerializer.toObject as any,
  });

  if (activateJobPostingError) {
    return <ErrorMessage />;
  }

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          display: {xs: 'block', sm: 'flex'},
          justifyContent: 'space-between',
          paddingBottom: '16px',
        }}
      >
        <Grid
          item
          xs={12}
          sm={6}
          md={6}
          sx={{
            paddingBottom: {xs: '16px', sm: '0px'},
          }}
        >
          <Typography
            variant='h2'
            sx={{
              wordWrap: 'break-word',
              textAlign: {xs: 'center', sm: 'left'},
            }}
          >
            {jobPosting?.title}
          </Typography>
        </Grid>
        {!jobPosting.archivedAt ? (
          <Grid
            container
            item
            xs={12}
            sm={8}
            marginLeft={{xs: '0px', sm: '2px'}}
            sx={{
              display: 'flex',
              justifyContent: {xs: 'center', sm: 'flex-end'},
            }}
          >
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: {xs: 'center', sm: 'flex-end'},
                alignItems: 'center',
                justifyItems: 'center',
                paddingBottom: {xs: '12px', sm: '4px', md: '0px'},
                marginRight: {xs: '0px', sm: '4px', md: '16px'},
              }}
            >
              <EmpionTooltip
                title={
                  !jobPosting?.publishedAt
                    ? t('companyJobPostings.jobPostingsAccordion.buttons.publishedOnlyTooltip')
                    : ''
                }
                isEnabled={!jobPosting?.publishedAt}
              >
                <Box>
                  <EmpionButton
                    onClick={handleViewApplicantsClick}
                    disabled={jobPosting?.publishedAt ? false : true}
                    sx={{
                      ...buttonThemeColour,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                      '&.Mui-disabled': {
                        backgroundColor: empionGreen[40],
                      },
                    }}
                  >
                    {t('companyJobPostings.jobPostingsAccordion.buttons.viewApplicants')}
                  </EmpionButton>
                </Box>
              </EmpionTooltip>
            </Grid>
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: {xs: 'center', sm: 'flex-end'},
                alignItems: 'center',
                justifyItems: 'center',
                paddingBottom: {xs: '12px', sm: '4px', md: '0px'},
                marginRight: {xs: '0px', sm: '4px', md: '16px'},
              }}
            >
              <MenuButtonActions
                companyId={companyId}
                jobPosting={jobPosting}
                handleUpdateJobPosting={handleUpdateJobPosting}
                handleDeleteJobPosting={handleDeleteJobPosting}
                handleJobPostingStatusChange={handleJobPostingStatusChange}
                companyLocations={companyLocations}
              />
            </Grid>
          </Grid>
        ) : (
          <EmpionButton
            onClick={() => activateJobPosting()}
            sx={{
              ...buttonThemeColour,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              '&.Mui-disabled': {
                backgroundColor: empionGreen[40],
              },
            }}
          >
            {t('companyJobPostings.jobPostingsAccordion.buttons.activate')}
          </EmpionButton>
        )}
      </Grid>
      <Grid
        item
        sx={{
          display: {xs: 'block', md: 'flex'},
          gap: '10px',
          justifyContent: 'start',
          paddingBottom: '16px',
        }}
      >
        <Grid
          item
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Typography variant='h4'>{jobPosting?.company}</Typography>
        </Grid>
        <Grid
          item
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: {xs: '0px'},
          }}
        >
          <Typography variant='body1' color={locationsAndDateColorBody1}>
            {utils.formatLocations(jobPosting, currentLanguage)}
          </Typography>
        </Grid>
        <Grid display={'flex'} alignItems={'center'} gap={1}>
          <Grid
            item
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Typography sx={{marginLeft: {xs: '0px'}}} variant='body1' color={locationsAndDateColorBody1}>
              {transformDate(jobPosting?.updatedAt || '', 'long')}
            </Typography>
            <JobPostingStatus jobPosting={jobPosting} color={locationsAndDateColorBody1} />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        display='flex'
        justifyContent='space-between'
        marginLeft={0.5}
        sx={{
          flexDirection: {xs: 'column', md: 'row'},
        }}
      >
        <Column md={4}>
          <DepartmentDisplay departments={jobPosting.departments || []} />
        </Column>
        <Column md={4}>
          <IconHeader iconColor={iconColor} icon={JobDetailsIcon.MoneyBill} text={utils.formatSalary(jobPosting)} />
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.RemoteWork}
            text={utils.formatWorkMode(jobPosting, t)}
          />
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.Knowledge}
            text={utils.formatExperienceAndLeaderShip(
              jobPosting,
              t,
              jobPosting?.experience?.yearsOfExperience ? jobPosting?.experience?.yearsOfExperience > 1 : false,
              jobPosting?.experience?.yearsOfLeadership ? jobPosting?.experience?.yearsOfLeadership > 1 : false
            )}
          />
        </Column>
        <Column md={4}>
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.BuildingColumns}
            text={utils.formatDegree(jobPosting, t)}
          />
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.Clock}
            text={utils.formatEmploymentType(jobPosting, t)}
          />
          <IconHeader
            iconColor={iconColor}
            icon={JobDetailsIcon.Comment}
            text={utils.formatLanguages(jobPosting, currentLanguage)}
          />
        </Column>
      </Grid>
    </Grid>
  );
}
